import { faChevronDown } from "@jewlr/pro-solid-svg-icons/faChevronDown"
import { faChevronUp } from "@jewlr/pro-solid-svg-icons/faChevronUp"
import {
  Box,
  Button,
  Divider,
  Flex,
  FontAwesomeIcon,
  Image,
  Typography,
} from "@jewlr/storybook/core"
import { useHandleOutsideClick } from "@jewlr/storybook/helpers"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import React, { useState, useRef } from "react"
import styled from "styled-components"

import useNavigation from "helpers/use-navigation"
import withCountrySwitch from "hoc/with-country-switch"
import countries from "site-data/jewlr/countries"

const SelectedCountry = styled(Button).attrs((props) => ({
  bg: props.expanded ? "#2a2a2a" : "inherit",
  display: "block",
  variant: "text",
  width: "100%",
}))`
  padding: 16px 16px 0;
  text-align: left;

  &:focus-visible {
    outline: ${(props) => props.theme.colors.white} solid 2px;
  }
`

const SectionTitle = styled(Typography).attrs((props) => ({
  alignItems: "center",
  color: props.theme.colors.white,
  display: "inline-flex",
  fontSize: "14px",
  fontWeight: { _: "500", desktop: "600" },
  justifyContent: { _: "center", tablet: "flex-start" },
  lineHeight: "16px",
}))``

const CountryDropdown = styled.div`
  background: #2a2a2a;
  padding: 0 16px 16px;
  width: 100%;
  z-index: 9999;
  ${(props) => props.theme.mediaQueries.tablet`
    position: absolute;
  `}
`

const CountryName = styled(Typography)`
  color: ${(props) => props.theme.colors.white};
  font-size: 12px;
  font-weight: 500;
  text-align: left;
  width: 100%;
`

const CountryButton = styled(Button).attrs({
  display: "flex",
  variant: "text",
  width: "100%",
})`
  margin-bottom: 10px;
  padding: 4px 0;

  &:focus {
    outline: 1px solid ${(props) => props.theme.colors.white};
    outline-offset: 1px;
  }

  &:hover p {
    text-decoration: underline;
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`

const CountryOption = ({ closeDropdown, country, index, onClick }) => {
  const keyboardProps = useNavigation({ onEscape: closeDropdown })

  return (
    <CountryButton
      aria-selected="false"
      onClick={onClick}
      role="option"
      tabIndex={index === 0 ? "0" : "-1"}
      {...keyboardProps}
    >
      <Image
        alt={country.full_country_name}
        aria-hidden
        data-src={`${sl.config.jewlr_asset_url}images/icons/flags/${country.site_country}.svg`}
        height="16px"
        mr="10px"
        src={require("images/icons/jewlr.gif")}
        width="24px"
      />
      <CountryName>{country.full_country_name}</CountryName>
    </CountryButton>
  )
}

CountryOption.propTypes = {
  closeDropdown: PropTypes.func,
  country: PropTypes.object,
  index: PropTypes.number,
  onClick: PropTypes.func,
}

const CountrySelector = ({ doSwitch }) => {
  const countrySelectorRef = useRef()
  const [expanded, showOptions] = useState(false)

  const currentCountry = countries.find(
    ({ geo }) => geo === sl.config.site_country_for_analytics
  )
  useHandleOutsideClick({
    enabled: expanded,
    onOutsideClick: () => showOptions(false),
    ref: countrySelectorRef,
  })

  return (
    <Box
      aria-label="Change Country"
      position="relative"
      ref={countrySelectorRef}
      width="186px"
    >
      <SelectedCountry
        aria-expanded={expanded}
        aria-haspopup="listbox"
        expanded={expanded}
        id="country-selector-button"
        onClick={() => showOptions(!expanded)}
      >
        <SectionTitle>
          CHANGE COUNTRY{" "}
          <FontAwesomeIcon
            color="white"
            fontSize="8px"
            icon={expanded ? faChevronUp : faChevronDown}
            ml={1}
          />
        </SectionTitle>
        <Box py="10px">
          <Flex
            alignItems="center"
            py="10px"
            width={{ _: "136px", tablet: "100%" }}
          >
            <Image
              alt={currentCountry.full_country_name}
              aria-hidden
              data-src={`${sl.config.jewlr_asset_url}images/icons/flags/${currentCountry.site_country}.svg`}
              height="16px"
              mr="10px"
              src={require("images/icons/jewlr.gif")}
              width="24px"
            />
            <CountryName>{currentCountry.full_country_name}</CountryName>
          </Flex>
        </Box>
      </SelectedCountry>
      {expanded && (
        <>
          <FocusScope contain restoreFocus>
            <CountryDropdown
              aria-labelledby="country-selector-button"
              id="country-selector-list"
              role="listbox"
            >
              <Divider bg="white" mb="10px" mt="0" />
              <Box mt="4px">
                {countries
                  .filter(
                    ({ geo }) => geo !== sl.config.site_country_for_analytics
                  )
                  .map((country, index) => (
                    <CountryOption
                      closeDropdown={() => showOptions(false)}
                      country={country}
                      index={index}
                      key={country.geo}
                      onClick={() => doSwitch(country.geo)}
                    />
                  ))}
              </Box>
            </CountryDropdown>
          </FocusScope>
        </>
      )}
    </Box>
  )
}

CountrySelector.propTypes = {
  doSwitch: PropTypes.func,
}

export default withCountrySwitch(CountrySelector)
